import {
  FormControl,
  FormHelperText,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import React, { useMemo } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { Link as RouterLink } from 'react-router-dom'

import TestTeamExternalIntegration from 'components/rbac-settings/teams/TestTeamExternalIntegration'
import { URLHelperText } from 'components/services/create/URLHelperText'
import { StatusCheckSuccessEvaluation } from 'components/status-checks/form/status-check/StatusCheckSuccessEvaluation'
import {
  buildTeamExternalIntegration,
  getExistingStatusCheckByMonitorUrl,
  StatusCheckFormValues
} from 'components/status-checks/status-checks-form-utils'
import { getStatusChecksUrls } from 'components/status-checks/status-checks-urls-utils'
import { useStatusCheckDetailsContext } from 'context/status-checks/StatusCheckDetailsContext'
import { useStatusChecksContext } from 'context/status-checks/StatusChecksContext'
import { useTranslation } from 'hooks/use-translation'
import { ObservabilityToolType } from 'types/api/status-checks/status-check'

export const AWSMonitorSuccessEvaluation: React.FC = () => {
  const { trans } = useTranslation()
  const { control, formState, getValues } =
    useFormContext<StatusCheckFormValues>()

  const [monitorUrl, observabilityTool, externalIntegration] =
    useWatch<StatusCheckFormValues>({
      control,
      name: ['monitorUrl', 'observabilityTool', 'externalIntegration']
    })
  const statusChecksUrls = getStatusChecksUrls()

  const isCustomObservabilityTool =
    observabilityTool.type === ObservabilityToolType.CUSTOM

  const isAWS = observabilityTool.type === ObservabilityToolType.AWS

  const {
    handleSuccess,
    handleTestConnectionError,
    isStatusCheckConnectionValidated,
    isStatusCheckEvaluationValidated,
    isTestStatusCheckDisabled,
    setIsStatusCheckConnectionValidated,
    setIsStatusCheckEvaluationValidated
  } = useStatusCheckDetailsContext()

  const { statusChecks: existingStatusChecks } = useStatusChecksContext()

  const existingStatusCheck = useMemo(() => {
    return getExistingStatusCheckByMonitorUrl(
      existingStatusChecks,
      observabilityTool,
      monitorUrl
    )
  }, [existingStatusChecks, monitorUrl, observabilityTool])

  return (
    <Stack spacing={3}>
      <FormControl>
        <Typography mb={2} mt={2}>
          {trans('forms.save_status_check.connect_monitor.monitor_url.prompt')}
        </Typography>
        <Controller
          control={control}
          name="monitorUrl"
          render={({ field }) => (
            <TextField
              data-testid="status-check-monitor-url-input"
              {...field}
              aria-describedby="monitor-url-helper-text"
              error={Boolean(formState.errors.monitorUrl)}
              helperText={formState.errors.monitorUrl?.message}
              label={trans(
                'forms.save_status_check.connect_monitor.monitor_url.text_field.label'
              )}
              variant="outlined"
              onChange={(e) => {
                // Need to reset validation states on monitor URL change
                // TODO: reset results displays for test components
                if (isStatusCheckConnectionValidated) {
                  setIsStatusCheckConnectionValidated(false)
                }
                if (isStatusCheckEvaluationValidated) {
                  setIsStatusCheckEvaluationValidated(false)
                }

                field.onChange(e)
              }}
            />
          )}
        />
        {formState.errors.monitorUrl?.type === 'notOneOf' && (
          <FormHelperText id="link-to-existing-health-check">
            <Typography variant="caption">
              <RouterLink to={statusChecksUrls.edit(existingStatusCheck?.id)}>
                {trans(
                  'forms.save_status_checks.monitor_url.existing_status_check.link.text'
                )}
              </RouterLink>
            </Typography>
          </FormHelperText>
        )}
        <FormHelperText id="monitor-url-helper-text">
          <URLHelperText integration={externalIntegration} />
        </FormHelperText>

        {(isCustomObservabilityTool || isAWS) && (
          <Stack mt={2} spacing={3}>
            <TestTeamExternalIntegration
              authenticate={false}
              buttonText="Test Connection"
              disabled={isTestStatusCheckDisabled}
              integration={buildTeamExternalIntegration(getValues())}
              onError={handleTestConnectionError}
              onResponseReceived={handleSuccess}
            />
            <StatusCheckSuccessEvaluation />
          </Stack>
        )}
      </FormControl>
    </Stack>
  )
}
